'use client';

import SvgChevrondown from '@common/__generated__/icon/SvgChevrondown';
import { Button } from '@common/components';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import useEmblaCarousel from 'embla-carousel-react';
import React, { useCallback, useEffect, useState } from 'react';

import { UnauthQueryOptions } from '@/api';
import { AMBASSADOR_NOTICE } from '@/app/donation/_components/ReviewCard';
import { toTextPreview } from '@/app/donation/_utils/text';
import { Image, LoadingPageTemplate } from '@/components/client';
import { Rating } from '@/components/molecule/Rating';

interface ReviewDetailContentProps {
  reviewId?: number;
}

export const REVIEW_DETAIL_CONTENT_QUERY_KEY =
  'get:/wello/v2/allows/home/gift-review/{reviewId}';

export const ReviewDetailContent = ({ reviewId }: ReviewDetailContentProps) => {
  const [isAnswerExpanded, setIsAnswerExpanded] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
  });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- embla 캐러셀 select 이벤트에서 현재 이미지 인덱스 동기화
    onSelect();
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  const { data: review, isLoading } = useQuery({
    ...UnauthQueryOptions[REVIEW_DETAIL_CONTENT_QUERY_KEY]({
      path: {
        reviewId: reviewId!,
      },
    }),
    enabled: !!reviewId,
    select: (res) => res.context,
  });

  const answerPreview = toTextPreview(
    review?.answer ?? '',
    ANSWER_EXPANDED_POINT,
  );
  const isPhotoReview = review?.fileUrlList && review.fileUrlList.length > 0;

  if (isLoading) {
    return <LoadingPageTemplate blur fadeIn />;
  }

  return (
    <div
      className={cn('relative flex size-full gap-20', 'mobile-tablet:flex-col')}
    >
      {isPhotoReview && (
        <div
          className={cn(
            'min-w-0 shrink-0 basis-[544px]',
            'mobile-tablet:width-full mobile-tablet:basis-0',
          )}
        >
          <div className="relative aspect-square w-full">
            <div className="size-full overflow-hidden" ref={emblaRef}>
              <div className="flex h-full">
                {review?.fileUrlList.map((url, index) => (
                  <div
                    key={index}
                    className="relative aspect-square min-w-0 flex-[0_0_100%]"
                  >
                    <Image
                      fill
                      src={url}
                      alt="답례품 후기 이미지"
                      className="object-cover object-center"
                      sizes="850px"
                    />
                  </div>
                ))}
              </div>
            </div>
            {review?.fileUrlList && review.fileUrlList.length > 1 && (
              <div className="absolute bottom-0 right-0 mb-16 ml-auto mr-16 w-fit rounded-pill bg-Gray-black/20 px-8 py-2 font-600 text-Gray-white typo-caption2">
                {selectedIndex + 1} / {review.fileUrlList.length}
              </div>
            )}
          </div>
        </div>
      )}
      <div
        className={cn(
          'flex flex-col gap-20',
          'mobile-tablet:px-20 mobile-tablet:pb-20',
        )}
      >
        <div className="flex flex-col">
          <Rating value={review?.rating ?? 0} size={16} className="mb-8" />
          <div className="flex items-center gap-4 font-400 text-Gray-550 typo-caption1">
            {review?.nickname}
            <div className="mb-6 h-10 w-1 bg-Gray-300" />
            {dayjs(review?.createdAt).format('YYYY.MM.DD')}
          </div>
          <p className="mb-16 break-all text-start font-400 text-Gray-600 typo-body4">
            {review?.productName}
          </p>
          <p className="whitespace-pre-line text-start font-500 text-Gray-800 typo-body3">
            {review?.contents}
          </p>
          {review?.orderType === 'TRIAL' ? (
            <p className="mt-12 text-start font-400 text-Gray-550 typo-caption1">
              {AMBASSADOR_NOTICE}
            </p>
          ) : null}
        </div>

        {answerPreview.text ? (
          <div className="flex flex-col rounded-medium bg-Gray-25 p-20">
            <div className="flex items-center justify-between pb-4">
              <p className="font-600 text-Gray-700 typo-body4">판매자</p>
              {review?.answerAt ? (
                <p className="font-400 text-Gray-550 typo-caption1">
                  {dayjs(review?.answerAt).format('YYYY.MM.DD')}
                </p>
              ) : null}
            </div>
            <p className="whitespace-pre-line pb-8 text-start font-400 text-Gray-700 typo-body4">
              {answerPreview.isTruncated ? (
                <>
                  {isAnswerExpanded
                    ? answerPreview.text
                    : `${answerPreview.preview}...`}
                  <Button
                    className="mt-8 flex size-fit items-center gap-4 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsAnswerExpanded((prev) => !prev);
                    }}
                    theme="sub-text"
                  >
                    <p className="font-600 text-Gray-550 typo-caption1">
                      {isAnswerExpanded ? '접기' : '더보기'}
                    </p>
                    <SvgChevrondown
                      className={cn(
                        'size-12 text-Gray-550 transition-transform duration-300',
                        {
                          '-rotate-180': isAnswerExpanded,
                        },
                      )}
                    />
                  </Button>
                </>
              ) : (
                answerPreview.text
              )}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ANSWER_EXPANDED_POINT = 60;
